/**
 * TrialBanner — Slim, elegant trial indicator.
 * Replaces the old aggressive orange bar with a tasteful strip.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Zap, X, AlertTriangle } from 'lucide-react';
import { usePlan } from '../context/PlanContext';
import { UpgradeModal } from './UpgradeModal';

function pad(n) {
  return String(n).padStart(2, '0');
}

export default function TrialBanner() {
  const { isTrial, isExpired, trialExpiresAt, isTrialEndingSoon } = usePlan();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('fitxero_trial_banner_dismissed') === 'true'
  );
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const calcTime = useCallback(() => {
    if (!trialExpiresAt) {
      return;
    }
    const ms = Math.max(0, new Date(trialExpiresAt) - new Date());
    setTimeLeft({
      days: Math.floor(ms / 86400000),
      hours: Math.floor((ms % 86400000) / 3600000),
      minutes: Math.floor((ms % 3600000) / 60000),
      seconds: Math.floor((ms % 60000) / 1000),
    });
  }, [trialExpiresAt]);

  useEffect(() => {
    if (!isTrial || isExpired || dismissed) {
      return;
    }
    calcTime();
    const t = setInterval(calcTime, 1000);
    return () => clearInterval(t);
  }, [isTrial, isExpired, dismissed, calcTime]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('fitxero_trial_banner_dismissed', 'true');
  };

  if (!isTrial || isExpired || dismissed) {
    return null;
  }
  const total =
    timeLeft.days * 86400 + timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;
  if (total <= 0) {
    return null;
  }

  const urgent = isTrialEndingSoon;

  return (
    <>
      <div
        data-testid="trial-banner"
        style={{
          height: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          gap: 12,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 500,
          background: urgent ? 'rgba(239,68,68,0.07)' : 'rgba(52,211,153,0.06)',
          borderBottom: `1px solid ${urgent ? 'rgba(239,68,68,0.18)' : 'rgba(52,211,153,0.15)'}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Left: status + message */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {urgent ? (
            <AlertTriangle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          ) : (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                background: 'hsl(158,84%,50%)',
                boxShadow: '0 0 6px rgba(52,211,153,0.6)',
              }}
              className="live-dot-anim"
            />
          )}
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: urgent
                ? 'rgba(252,165,165,0.95)'
                : 'color-mix(in srgb, hsl(158,84%,60%) 90%, white)',
            }}
          >
            {urgent
              ? `Trial ends in ${timeLeft.days}d ${pad(timeLeft.hours)}:${pad(timeLeft.minutes)} — upgrade to keep access.`
              : `Free trial active — ${timeLeft.days > 0 ? `${timeLeft.days}d ` : ''}${pad(timeLeft.hours)}:${pad(timeLeft.minutes)} remaining`}
          </span>
        </div>

        {/* Right: Upgrade CTA + dismiss */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => setUpgradeOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 14px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              background: urgent
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : 'linear-gradient(135deg, hsl(158,84%,44%), hsl(172,70%,38%))',
              color: '#fff',
              boxShadow: urgent
                ? '0 2px 10px rgba(239,68,68,0.35)'
                : '0 2px 10px rgba(52,211,153,0.3)',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = '';
            }}
          >
            <Zap size={11} /> Upgrade Now
          </button>

          <button
            onClick={handleDismiss}
            title="Dismiss"
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.35)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,0.35)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            }}
          >
            <X size={12} />
          </button>
        </div>
      </div>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} feature="Free Trial" />
    </>
  );
}
